/*
Incident Reporting (IRG) Service

Multi-tenancy: entity_id is stamped server-side from the JWT token
for all authenticated endpoints. The whistleblower endpoint is the one
exception — it accepts an entity_id in the body (it's a public endpoint).
*/

import apiClient from './apiService';
import { AxiosResponse } from 'axios';

export interface ApiOk<T> {
  success: boolean;
  data: T;
  message?: string;
  meta?: { page: number; limit: number; total: number; pages: number };
}

// ---------- Master types ----------
export type WfSaveTarget = 'draft' | 'submit' | 'approve';

export interface IRGProcess {
  id: number; entity_id: number; process_name: string;
  process_description?: string | null;
  is_active: number; is_approved: number; workflow_state?: string;
}
export interface IRGRisk {
  id: number; entity_id: number; risk_name: string;
  risk_description?: string | null;
  is_active: number; is_approved: number; workflow_state?: string;
}
export interface IRGCategory {
  id: number; entity_id: number; category_name: string;
  description?: string | null;
  is_active: number; is_approved: number; workflow_state?: string;
}
export interface IRGIncidentType {
  id: number; entity_id: number; incident_name: string;
  incident_category_id: number; description?: string | null;
  is_active: number; is_approved: number; workflow_state?: string;
}

// ---------- Incident types ----------
export interface Incident {
  id: number;
  entity_id: number;
  incident_code: string;
  incident_description: string;
  occurrence_type?: string | null;
  incident_category_id?: number | null;
  incident_master_id?: number | null;
  risk_id?: number | null;
  process_id?: number | null;
  department_id?: number | null;
  reported_by_user_id?: number | null;
  reported_by_user_name?: string | null;
  remediation_status: string;
  reporter_feedback?: string | null;
  is_active: number;
  created_on?: string;
  modified_on?: string;
  // System-generated code (e.g. "INC-1") — set on create
  code?: string | null;
}

export type TriagePayload =
  | {
      decision: 'trivial';
      reporter_feedback: string;
    }
  | {
      decision: 'escalate';
      risk_severity: string;
      capa_owner: string;            // display name (kept for UI feedback strings)
      capa_owner_id: number;          // USR_users.id — becomes issue_head_user_id
      instructions: string;
      target_date?: string;
    }
  | {
      decision: 'task';
      task_title: string;
      task_assignee_id: number;             // USR_users.id of the doer (required)
      task_assignee?: string;               // display name (for UI feedback strings)
      task_due_date?: string;
      task_description?: string;
      task_grace_period_days?: number;
      task_approver_id?: number;
      task_validator_id?: number;
      task_priority?: string;               // LOW | MEDIUM | HIGH | CRITICAL
    };

// ---------- Helper ----------
function unwrap<T>(resp: AxiosResponse<ApiOk<T>>): T {
  if (!resp.data?.success) {
    throw new Error(resp.data?.message || 'Request failed');
  }
  return resp.data.data;
}

// ---------- Generic master CRUD factory ----------
function masterApi<TCreate, TItem>(path: string) {
  return {
    list: async (params?: { search?: string; status?: 'ALL' | 'ACTIVE' | 'INACTIVE'; page?: number; limit?: number }): Promise<TItem[]> => {
      const qs = new URLSearchParams();
      if (params?.search) qs.append('search', params.search);
      if (params?.status) qs.append('status', params.status);
      if (params?.page) qs.append('page', String(params.page));
      if (params?.limit) qs.append('limit', String(params.limit));
      const url = `/api/v1/irg/${path}${qs.toString() ? `?${qs}` : ''}`;
      const resp = await apiClient.get<ApiOk<TItem[]>>(url);
      return unwrap(resp);
    },
    create: async (payload: TCreate, action: WfSaveTarget = 'draft'): Promise<TItem> => {
      const resp = await apiClient.post<ApiOk<TItem>>(`/api/v1/irg/${path}?action=${action}`, payload);
      return unwrap(resp);
    },
    update: async (id: number, payload: Partial<TCreate> & { is_active?: number }): Promise<TItem> => {
      const resp = await apiClient.put<ApiOk<TItem>>(`/api/v1/irg/${path}/${id}`, payload);
      return unwrap(resp);
    },
    remove: async (id: number): Promise<void> => {
      await apiClient.delete(`/api/v1/irg/${path}/${id}`);
    },
  };
}

// Dropdown source: approved + active only ({total, eligible, items} — NOT wrapped in ApiOk)
export interface DropdownResp {
  total: number;
  eligible: number;
  items: { id: number; name: string; code?: string }[];
}

export async function fetchIncidentTypesDropdown(): Promise<DropdownResp> {
  const res = await apiClient.get<DropdownResp>('/api/v1/irg/incident-types/dropdown');
  return res.data;
}

export async function fetchRisksDropdown(): Promise<DropdownResp> {
  const res = await apiClient.get<DropdownResp>('/api/v1/irg/risks/dropdown');
  return res.data;
}

export async function fetchProcessesDropdown(): Promise<DropdownResp> {
  const res = await apiClient.get<DropdownResp>('/api/v1/irg/processes/dropdown');
  return res.data;
}

export async function fetchCategoriesDropdown(): Promise<DropdownResp> {
  const res = await apiClient.get<DropdownResp>('/api/v1/irg/categories/dropdown');
  return res.data;
}

// 4 master CRUD APIs
export const processesApi = masterApi<{ process_name: string; process_description?: string }, IRGProcess>('processes');
export const risksApi = masterApi<{ risk_name: string; risk_description?: string }, IRGRisk>('risks');
export const categoriesApi = masterApi<{ category_name: string; description?: string }, IRGCategory>('categories');
export const incidentTypesApi = masterApi<{ incident_name: string; incident_category_id: number; description?: string }, IRGIncidentType>('incident-types');

// ---------- Insights ----------
export interface IRGInsights {
  needs_triage: number;
  open_investigations: number;
  avg_age_days: number;
  closed_this_month: number;
  type_distribution: { label: string; count: number }[];
  department_heatmap: { label: string; risk: 'High' | 'Medium' | 'Low'; percent: number; count: number }[];
}

export interface IRGDepartment {
  id: number;
  name: string;
}

export const departmentsApi = {
  list: async (): Promise<IRGDepartment[]> => {
    const resp = await apiClient.get<ApiOk<IRGDepartment[]>>('/api/v1/irg/departments');
    return unwrap(resp);
  },
};

export async function fetchDepartmentsDropdown(): Promise<DropdownResp> {
  const res = await apiClient.get<DropdownResp>('/api/v1/irg/departments/dropdown');
  return res.data;
}

export const insightsApi = {
  get: async (): Promise<IRGInsights> => {
    const resp = await apiClient.get<ApiOk<IRGInsights>>('/api/v1/irg/insights');
    return unwrap(resp);
  },
};

// ---------- Incidents ----------
export const incidentsApi = {
  list: async (params?: {
    archived?: boolean;
    search?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<Incident[]> => {
    const qs = new URLSearchParams();
    if (params?.archived) qs.append('archived', 'true');
    if (params?.search) qs.append('search', params.search);
    if (params?.status) qs.append('status', params.status);
    if (params?.page) qs.append('page', String(params.page));
    if (params?.limit) qs.append('limit', String(params.limit));
    const url = `/api/v1/irg/incidents${qs.toString() ? `?${qs}` : ''}`;
    const resp = await apiClient.get<ApiOk<Incident[]>>(url);
    return unwrap(resp);
  },

  get: async (id: number): Promise<Incident> => {
    const resp = await apiClient.get<ApiOk<Incident>>(`/api/v1/irg/incidents/${id}`);
    return unwrap(resp);
  },

  create: async (payload: {
    incident_description: string;
    occurrence_type?: string;
    incident_category_id?: number;
    incident_master_id?: number;
    risk_id?: number;
    process_id?: number;
    department_id?: number;
  }): Promise<Incident> => {
    const resp = await apiClient.post<ApiOk<Incident>>('/api/v1/irg/incidents', payload);
    return unwrap(resp);
  },

  /** Public endpoint - no auth required */
  whistleblower: async (payload: {
    entity_id: number;
    incident_description: string;
    occurrence_type?: string;
    incident_category_id?: number;
    incident_master_id?: number;
    risk_id?: number;
    process_id?: number;
    is_anonymous: boolean;
    reported_by_user_id?: number;
  }): Promise<{ id: number; incident_code: string }> => {
    const resp = await apiClient.post<ApiOk<{ id: number; incident_code: string }>>(
      '/api/v1/irg/incidents/whistleblower',
      payload,
    );
    return unwrap(resp);
  },

  triage: async (id: number, payload: TriagePayload): Promise<Incident> => {
    const resp = await apiClient.patch<ApiOk<Incident>>(`/api/v1/irg/incidents/${id}/triage`, payload);
    return unwrap(resp);
  },

  archive: async (id: number): Promise<Incident> => {
    const resp = await apiClient.patch<ApiOk<Incident>>(`/api/v1/irg/incidents/${id}/archive`);
    return unwrap(resp);
  },

  update: async (id: number, payload: Partial<Incident>): Promise<Incident> => {
    const resp = await apiClient.put<ApiOk<Incident>>(`/api/v1/irg/incidents/${id}`, payload);
    return unwrap(resp);
  },

  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/v1/irg/incidents/${id}`);
  },
};
